// Décor du viseur : silhouettes qui se découpent sur l'horizon (ligne y = 0 du monde).
// Deux plans en parallaxe : le lointain (défile moins vite que la caméra) et le premier plan.
// Chaque niveau a son décor (voir sceneryThemeForLevel) : ville, plage, forêt de nuit, montagnes,
// jungle, cimetière — puis on recommence. Fonctions pures ; le générateur aléatoire est injectable.
// Les couleurs de chaque décor sont dans config/theme.js (sceneryPalettes), le dessin dans
// ui/sceneryPainter.js.
import { wrapX } from './camera.js';

export const HORIZON_Y = 0;          // ligne d'horizon, en unités monde
// Le plan lointain défile à 70 % de la vitesse de la caméra : un tour complet (worldSize) en montre donc
// 0,7 × worldSize de décor, soit 4 à 8 écrans d'horizon selon le niveau — assez long pour un vrai 360°.
// Chaque plan boucle sur sa propre période (worldSize × parallax) : la caméra revient d'où elle est partie
// quand elle fait le tour du monde, et le côté droit du décor se raccorde au côté gauche.
export const FAR_PARALLAX = 0.7;
export const NEAR_PARALLAX = 1;

// Sol : tout ce qui est sous l'horizon est une projection de ce qu'on regarde — plus on baisse
// l'objectif, plus on voit près de soi, jusqu'à ses chaussures. Pas d'objets : une texture et du
// relief, en fausse perspective. Leur taille suit la profondeur (groundScale), ce qui est couché sur
// le sol s'aplatit vers l'horizon (groundForeshortening), et le tout est réparti en bandes de
// profondeur qui défilent chacune à leur vitesse (parallax, > 1 : plus proche que le premier plan),
// comme les calques des vieux dessins animés. Bandes en fractions de worldSize / 2 (le bas du monde =
// la butée basse de l'objectif = les pieds) ; mounds = nombre de bosses de relief dans la bande.
export const GROUND_BANDS = Object.freeze([
  { id: 'horizon', from: 0.012, to: 0.15, parallax: 1.0, mounds: 7 },
  { id: 'middle', from: 0.15, to: 0.42, parallax: 1.15, mounds: 6 },
  { id: 'near', from: 0.42, to: 0.72, parallax: 1.35, mounds: 5 },
  { id: 'feet', from: 0.72, to: 1.0, parallax: 1.6, mounds: 4 },
]);

/**
 * Repères posés sur le sol selon le thème (les autres n'en ont pas) : nombre de chaque sorte par bande, de
 * l'horizon aux pieds. Une plage lisse n'a pas de bosses (GROUND_FLAT), mais des crabes ; la forêt a
 * des champignons et des mousses, la montagne des pierres, des edelweiss et des cairns, la jungle des racines, des lianes et de grosses araignées,
 * le cimetière (dernier niveau) des croix et des tombes très nombreuses et grosses, et des os. `size` = largeur à l'échelle 1 (unités monde).
 */
export const GROUND_PROPS = Object.freeze({
  beach: { crab: { counts: [1, 2, 2, 1], size: 26 } },
  mountains: {
    stone: { counts: [2, 3, 3, 2], size: 22 },
    edelweiss: { counts: [1, 3, 3, 2], size: 20 },
    cairn: { counts: [0, 1, 1, 1], size: 30 },
  },
  jungle: {
    root: { counts: [1, 2, 3, 2], size: 130 },
    vine: { counts: [1, 2, 2, 2], size: 100 },
    // Grosses araignées qui trottent au sol, surtout dans les bandes proches de l'objectif
    spider: { counts: [0, 3, 6, 9], size: 31 },
  },
  // Dernier niveau : beaucoup plus de croix et de tombes, et beaucoup plus grosses
  graveyard: {
    cross: { counts: [7, 10, 13, 10], size: 60 },
    tomb: { counts: [4, 7, 10, 7], size: 96 },
    bone: { counts: [0, 0, 1, 1], size: 32 },
  },
  forest: { moss: { counts: [2, 3, 3, 2], size: 70 }, mushroom: { counts: [1, 3, 3, 2], size: 24 } },
});
export const GROUND_FLAT = Object.freeze(['beach']);

/** Échelle à la profondeur `frac` (y / (worldSize / 2)) : proportionnelle à la distance sous l'horizon. */
export const groundScale = (frac) => 0.35 + 1.9 * frac;

/** Aplatissement de ce qui est couché sur le sol : très écrasé près de l'horizon, presque vu de dessus aux pieds. */
export const groundForeshortening = (frac) => 0.6 + 1.4 * frac;

/** Un décor par niveau, dans cet ordre ; au-delà du dernier, on reprend au premier. */
export const SCENERY_THEMES = Object.freeze(['city', 'beach', 'forest', 'mountains', 'jungle', 'graveyard']);

/** Décor du niveau `level` (1 → ville, 2 → plage, … 7 → ville à nouveau). */
export function sceneryThemeForLevel(level) {
  const n = Math.max(1, Math.floor(level) || 1) - 1;
  return SCENERY_THEMES[n % SCENERY_THEMES.length];
}

/**
 * @typedef {{ kind:string, x:number, w:number, h:number, lift?:number, flip?:boolean, phase?:number,
 *   [extra:string]: any }} Prop   x = bord gauche ; lift = hauteur du pied au-dessus de l'horizon
 * @typedef {{ kind:'crescent'|'full'|'sun', x:number, y:number, r:number }} SkyBody
 * @typedef {{ kind:'bird'|'bat', x:number, y:number, s:number, phase:number }} Flyer
 * @typedef {{ kind:'crab'|'mushroom'|'moss'|'stone'|'edelweiss'|'cairn'|'root'|'vine'|'spider'|'tomb'|'cross'|'bone', x:number, y:number, w:number, parallax:number, phase:number, flip:boolean }} GroundProp
 *   repère posé sur le sol : même repère que les bosses, w = largeur (déjà à l'échelle de sa profondeur),
 *   phase = décalage de son animation (trottinade, pulsation) et de sa forme
 * @typedef {{ x:number, y:number, w:number, h:number, parallax:number }} Mound   bosse de relief : x = centre dans le
 *   repère de sa bande (période worldSize × parallax), y = profondeur (unités monde sous l'horizon),
 *   w = largeur, h = demi-hauteur (déjà à l'échelle de sa profondeur)
 * @typedef {{ theme:string, far:Prop[], near:Prop[], body:SkyBody, stars:{x:number,y:number,r:number,phase:number}[],
 *   flyers:Flyer[], sea:{depth:number}|null,
 *   milkyWay:{ x0:number, width:number, yc:number, ry:number, bow:number },
 *   plane:{ y:number, x0:number, span:number, speed:number, s:number, phase:number },
 *   ground:{ worldSize:number, seed:number, mounds:Mound[], props:GroundProp[] } }} Scenery
 */

/**
 * Génère le décor `theme` pour un monde de côté `worldSize`. Le plan lointain couvre la largeur
 * parcourue par la caméra × parallaxe ; le premier plan couvre tout le monde, avec de la marge.
 * Les coordonnées du ciel (astre, étoiles, oiseaux) sont celles du plan lointain.
 * @param {{ worldSize:number, theme?:string, random?:() => number }} options
 * @returns {Scenery}
 */
export function generateScenery({ worldSize, theme = 'city', random = Math.random }) {
  const rnd = (min, max) => min + random() * (max - min);
  const c = {
    random,
    rnd,
    halfNear: worldSize / 2, // demi-période du premier plan (parallax 1) : le décor tient exactement dans [-halfNear, halfNear)
    halfFar: (worldSize / 2) * FAR_PARALLAX, // idem pour le plan lointain, et pour tout ce qui est dans son ciel
  };
  const name = BUILDERS[theme] ? theme : 'city';
  const built = BUILDERS[name](c);
  // Ciel : des étoiles sur toute la hauteur que l'objectif peut atteindre (quelques-unes, hautes, quand le soleil
  // couchant éclaire encore l'horizon), une voie lactée dense en travers, et un avion lointain qui traverse
  const sky = SKY_STARS[name];
  const top = sky.top;
  const bottom = c.halfNear; // tout en haut du monde : butée haute de l'objectif
  const milkyWay = generateMilkyWay(c, built.body, top, bottom);
  return {
    theme: name, worldSize, flyers: [], sea: null, ...built,
    stars: [...starField(c, sky.count, top, bottom), ...milkyWayStars(c, milkyWay, top, bottom)],
    milkyWay,
    plane: generatePlane(c),
    ground: generateGround(c, name, worldSize),
  };
}

// --- Outils de génération ---------------------------------------------------------------

/**
 * Remplit [from, to) de gauche à droite : make(x) renvoie [élément, pas jusqu'au suivant]. Le décor boucle
 * (le bord droit se raccorde au bord gauche) : un élément qui dépasserait `to` serait recouvert par les
 * premiers, il est donc écarté — sauf si `overlap` (sommets qui se chevauchent par nature).
 */
function strip(from, to, make, overlap = false) {
  const items = [];
  for (let x = from; x < to;) {
    const [item, step] = make(x);
    if (!overlap && item.w !== undefined && x + item.w > to) break;
    items.push(item);
    x += step;
  }
  return items;
}

/**
 * Premier plan : éléments tirés au sort dans `table` ({ kind, weight, w:[min,max], h:[min,max],
 * extra?(c) }), posés côte à côte sans se chevaucher, séparés d'un écart tiré dans `gap`.
 */
function nearStrip(c, table, gap) {
  const total = table.reduce((sum, e) => sum + e.weight, 0);
  return strip(-c.halfNear, c.halfNear, (x) => {
    let roll = c.random() * total;
    const entry = table.find((e) => (roll -= e.weight) < 0) ?? table.at(-1);
    const prop = { kind: entry.kind, x, w: c.rnd(...entry.w), h: c.rnd(...entry.h), ...entry.extra?.(c) };
    return [prop, prop.w + c.rnd(...gap)];
  });
}

/**
 * Étoiles par thème : `count` pour un ciel de référence (1300 × 590), mis à l'échelle de la hauteur et de la
 * largeur réelles ; `top` = hauteur minimale au-dessus de l'horizon (haut du ciel seulement quand le soleil
 * couchant éclaire encore l'horizon).
 */
const SKY_STARS = {
  city: { count: 240, top: 40 }, beach: { count: 130, top: 280 }, forest: { count: 330, top: 30 },
  mountains: { count: 150, top: 240 }, jungle: { count: 130, top: 280 }, graveyard: { count: 200, top: 30 },
};
const SKY_REF = { width: 1300, height: 590 };

/** Champ d'étoiles dans le ciel du plan lointain, entre `top` et `bottom` unités au-dessus de l'horizon. */
function starField(c, count, top, bottom) {
  const n = Math.round(count * ((2 * c.halfFar) / SKY_REF.width) * ((bottom - top) / SKY_REF.height));
  return Array.from({ length: n }, () => ({
    x: c.rnd(-c.halfFar, c.halfFar), y: -c.rnd(top, bottom), r: c.rnd(0.5, 1.3), phase: c.rnd(0, 10),
  }));
}

/**
 * Voie lactée : une bande verticale, arrondie en fuseau (large au milieu, qui s'effile et se fond dans le ciel
 * aux deux bouts) et très légèrement courbe. Elle est centrée en `x0` (plan lointain) à la hauteur `yc`, de
 * demi-hauteur `ry` et de demi-largeur maximale `width` ; `bow` la fait bomber sur le côté au milieu. Elle est
 * toujours à au moins MILKY_WAY_GAP de l'astre (en tournant dans un sens comme dans l'autre), pour que leurs
 * lumières ne se mélangent pas. On la dessine en halo diffus et on la densifie en étoiles.
 */
export const MILKY_WAY_GAP = 380;
function generateMilkyWay(c, body, top, bottom) {
  // Le ciel boucle : on cherche à droite de l'astre, après l'écart, dans le reste de la période
  const period = c.halfFar * 2;
  const gap = Math.min(MILKY_WAY_GAP, period / 2 - 20);
  const x0 = wrapX(body.x + gap + c.random() * (period - 2 * gap), period);
  return {
    x0, width: c.rnd(85, 110), yc: -(top + bottom) / 2, ry: ((bottom - top) / 2) * 1.05, bow: c.rnd(-45, 45),
  };
}

/** Profil en fuseau de la voie lactée : 1 au milieu, 0 aux bouts (u de -1 à 1 le long de la bande). */
export const milkyWayTaper = (u) => Math.sqrt(Math.max(0, 1 - u * u));

/** Axe de la voie lactée à la hauteur y : décalage x0 + bombement. */
export function milkyWayAxis(mw, y) {
  const u = (y - mw.yc) / mw.ry;
  return mw.x0 + mw.bow * (1 - u * u);
}

/** Étoiles serrées le long de la voie lactée (écart à l'axe gaussien, de plus en plus étroit vers les bouts). */
function milkyWayStars(c, mw, top, bottom) {
  const n = Math.round(mw.ry * 2.4);
  const out = [];
  for (let i = 0; i < n; i++) {
    const y = mw.yc + c.rnd(-1, 1) * mw.ry;
    const u = (y - mw.yc) / mw.ry;
    if (-y < top || -y > bottom) continue;
    const spread = mw.width * (0.3 + 0.7 * milkyWayTaper(u)) * 1.1;
    out.push({
      x: milkyWayAxis(mw, y) + (c.random() + c.random() + c.random() - 1.5) * spread, // ~ gaussien
      y, r: c.rnd(0.35, 0.95), phase: c.rnd(0, 10),
    });
  }
  return out;
}

/** Avion lointain : traverse le ciel du plan lointain en boucle, dans un sens ou l'autre, avec ses feux clignotants. */
function generatePlane(c) {
  return {
    y: -c.rnd(110, 260), x0: c.rnd(-c.halfFar, c.halfFar), span: 2 * c.halfFar,
    speed: c.rnd(5, 9) * (c.random() < 0.5 ? -1 : 1), s: c.rnd(3.8, 4.6), phase: c.rnd(0, 10),
  };
}

/** Oiseaux (ou chauves-souris) qui volent dans le ciel du plan lointain, entre deux hauteurs. */
function flock(c, kind, count, [yMin, yMax], [sMin, sMax]) {
  return Array.from({ length: count }, () => ({
    kind, x: c.rnd(-c.halfFar, c.halfFar), y: -c.rnd(yMin, yMax), s: c.rnd(sMin, sMax), phase: c.rnd(0, 10),
  }));
}

// Attributs partagés : sens (animal tourné vers la gauche ou la droite) et déphasage d'animation
const facing = (c) => ({ flip: c.random() < 0.5, phase: c.rnd(0, 10) });

// --- Les décors -------------------------------------------------------------------------

const BUILDERS = {
  // Niveau 1 — la ville au crépuscule : immeubles au loin, arbres, maisons et église devant
  city(c) {
    const { rnd, random } = c;
    const far = strip(-c.halfFar, c.halfFar, (x) => {
      const w = rnd(18, 46);
      const h = rnd(30, 130);
      const windows = [];
      const cols = Math.max(1, Math.floor(w / 9));
      const rows = Math.max(1, Math.floor(h / 12));
      for (let col = 0; col < cols; col++) {
        for (let row = 0; row < rows; row++) {
          if (random() < 0.12) windows.push({ x: 3 + col * 9, y: 6 + row * 12 });
        }
      }
      return [{ kind: 'building', x, w, h, windows }, w + rnd(0, 6)];
    });
    const near = nearStrip(c, [
      { kind: 'round', weight: 42, w: [26, 46], h: [38, 70] },
      { kind: 'conifer', weight: 30, w: [18, 32], h: [50, 95] },
      { kind: 'house', weight: 14, w: [34, 60], h: [28, 44] },
      { kind: 'church', weight: 6, w: [30, 44], h: [90, 130] },
      { kind: 'grave', weight: 8, w: [8, 14], h: [10, 18] },
    ], [4, 40]);
    const body = { kind: 'crescent', x: rnd(-c.halfFar * 0.6, c.halfFar * 0.6), y: -rnd(150, 260), r: rnd(16, 24) };
    return { far, near, body };
  },

  // Niveau 2 — la plage au coucher du soleil : îlots et voiliers sur la mer, cocotiers devant
  beach(c) {
    const { rnd, random } = c;
    const far = strip(-c.halfFar, c.halfFar, (x) => {
      const item = random() < 0.55
        ? { kind: 'island', x, w: rnd(50, 140), h: rnd(6, 16), palm: random() < 0.6 }
        : { kind: 'sail', x, w: rnd(10, 16), h: rnd(14, 24), flip: random() < 0.5 };
      return [item, item.w + rnd(40, 170)];
    });
    const near = nearStrip(c, [
      { kind: 'palm', weight: 46, w: [34, 54], h: [80, 130], extra: () => ({ lean: rnd(-0.8, 0.8) }) },
      { kind: 'hut', weight: 8, w: [42, 60], h: [30, 42], extra: () => ({ lit: random() < 0.7 }) },
      { kind: 'bush', weight: 16, w: [14, 28], h: [8, 14] },
      { kind: 'rock', weight: 18, w: [12, 30], h: [6, 14] },
      { kind: 'crab', weight: 6, w: [8, 11], h: [5, 6], extra: facing },
      { kind: 'parasol', weight: 6, w: [26, 34], h: [26, 32], extra: facing },
    ], [8, 60]);
    // Soleil bas sur la mer, à demi couché : la mer (sea.depth) en cache le bas
    const body = { kind: 'sun', x: rnd(-100, 100), y: -rnd(8, 30), r: rnd(26, 34) };
    const flyers = flock(c, 'bird', 7, [70, 220], [4, 7]);
    return { far, near, body, flyers, sea: { depth: 16 } };
  },

  // Niveau 3 — la forêt de nuit : cerfs, renards, loups, hiboux et yeux qui brillent dans les buissons
  forest(c) {
    const { rnd, random } = c;
    const far = strip(-c.halfFar, c.halfFar, (x) => {
      const w = rnd(14, 28);
      return [{ kind: 'conifer', x, w, h: rnd(36, 82) }, w * rnd(0.4, 0.75)];
    });
    const near = nearStrip(c, [
      { kind: 'conifer', weight: 26, w: [22, 38], h: [80, 130] },
      { kind: 'oak', weight: 22, w: [44, 70], h: [70, 110] },
      { kind: 'bush', weight: 20, w: [18, 34], h: [10, 20], extra: (cc) => ({ eyes: random() < 0.45, phase: cc.rnd(0, 10) }) },
      { kind: 'deer', weight: 8, w: [30, 38], h: [32, 42], extra: facing },
      { kind: 'fox', weight: 7, w: [22, 28], h: [12, 15], extra: facing },
      { kind: 'wolf', weight: 6, w: [22, 28], h: [26, 32], extra: facing },
      { kind: 'owl', weight: 8, w: [14, 20], h: [40, 60], extra: facing },
    ], [2, 26]);
    const body = { kind: 'full', x: rnd(-120, 120), y: -rnd(130, 220), r: rnd(18, 24) };
    return { far, near, body };
  },

  // Niveau 4 — les montagnes : sommets enneigés, bouquetins sur les crêtes, aigles, sapins devant
  mountains(c) {
    const { rnd, random } = c;
    const peaks = strip(-c.halfFar, c.halfFar, (x) => {
      const w = rnd(150, 290);
      const h = rnd(70, 210);
      return [{ kind: 'peak', x, w, h, apex: rnd(0.35, 0.65), snow: h > 115 }, w * rnd(0.3, 0.55)];
    }, true);
    // Les plus hauts derrière : les sommets plus bas se découpent devant eux
    peaks.sort((a, b) => b.h - a.h);
    // Les sommets du bout débordent sur le début du panorama : la crête s'y prolonge, les bouquetins aussi
    const looped = [...peaks, ...peaks.map((p) => ({ ...p, x: p.x - c.halfFar * 2 }))];
    const goats = Array.from({ length: Math.max(4, Math.round((c.halfFar * 2) / 220)) }, () => {
      const x = rnd(-c.halfFar, c.halfFar);
      return { kind: 'ibex', x: x - 4, w: 8, h: 7, lift: ridgeHeight(looped, x) - 0.5, ...facing(c) };
    });
    const near = nearStrip(c, [
      { kind: 'conifer', weight: 44, w: [16, 30], h: [40, 90] },
      { kind: 'rock', weight: 26, w: [14, 40], h: [8, 22] },
      { kind: 'chalet', weight: 8, w: [38, 52], h: [30, 40], extra: () => ({ lit: random() < 0.8 }) },
      { kind: 'bush', weight: 14, w: [12, 24], h: [6, 12] },
    ], [6, 55]);
    // Soleil qui se couche derrière les sommets
    const body = { kind: 'sun', x: rnd(-110, 110), y: -rnd(100, 150), r: rnd(22, 28) };
    const flyers = flock(c, 'bird', 5, [160, 320], [5, 8]);
    return { far: [...peaks, ...goats], near, body, flyers };
  },

  // Niveau 5 — la jungle : canopée au loin, fougères, bananiers, fromagers et leurs lianes,
  // singes, toucans et yeux de jaguar dans les feuilles
  jungle(c) {
    const { rnd, random } = c;
    const far = strip(-c.halfFar, c.halfFar, (x) => {
      const emergent = random() < 0.12;
      const w = emergent ? rnd(50, 80) : rnd(30, 70);
      const h = emergent ? rnd(110, 150) : rnd(45, 95);
      return [{ kind: emergent ? 'emergent' : 'canopy', x, w, h }, w * rnd(0.4, 0.7)];
    });
    const near = nearStrip(c, [
      { kind: 'palm', weight: 16, w: [30, 46], h: [70, 110], extra: () => ({ lean: rnd(-0.5, 0.5) }) },
      { kind: 'bigleaf', weight: 26, w: [26, 44], h: [24, 42], extra: (cc) => ({ eyes: random() < 0.25, ...facing(cc) }) },
      { kind: 'fern', weight: 22, w: [22, 36], h: [14, 26] },
      { kind: 'kapok', weight: 16, w: [60, 90], h: [110, 160], extra: (cc) => ({ monkey: random() < 0.6, ...facing(cc) }) },
      { kind: 'toucan', weight: 8, w: [12, 16], h: [30, 42], extra: facing },
    ], [0, 14]);
    const body = { kind: 'sun', x: rnd(-110, 110), y: -rnd(70, 130), r: rnd(30, 38) };
    const flyers = flock(c, 'bird', 6, [120, 260], [4, 6]);
    return { far, near, body, flyers };
  },

  // Niveau 6 — le cimetière : chapelle en ruine et arbres morts au loin, tombes penchées, croix,
  // grilles, caveaux où luisent des yeux, corbeaux, chauves-souris et brume
  graveyard(c) {
    const { rnd, random } = c;
    const far = strip(-c.halfFar, c.halfFar, (x) => {
      const roll = random();
      let item;
      if (roll < 0.45) item = { kind: 'deadtree', x, w: rnd(30, 60), h: rnd(50, 90), ...facing(c) };
      else if (roll < 0.6) item = { kind: 'chapel', x, w: rnd(44, 64), h: rnd(70, 100), lit: random() < 0.6, ...facing(c) };
      else item = { kind: 'mound', x, w: rnd(60, 140), h: rnd(6, 14) };
      return [item, item.w + rnd(10, 70)];
    });
    const tilt = (cc) => ({ skew: cc.rnd(-0.14, 0.14) });
    const near = nearStrip(c, [
      { kind: 'grave', weight: 26, w: [10, 18], h: [14, 26], extra: tilt },
      { kind: 'cross', weight: 18, w: [10, 16], h: [20, 32], extra: tilt },
      { kind: 'obelisk', weight: 8, w: [10, 14], h: [36, 52] },
      { kind: 'fence', weight: 16, w: [40, 80], h: [16, 22] },
      { kind: 'deadtree', weight: 14, w: [40, 70], h: [70, 120], extra: (cc) => ({ crow: random() < 0.5, ...facing(cc) }) },
      { kind: 'crypt', weight: 8, w: [40, 56], h: [40, 56], extra: (cc) => ({ eyes: random() < 0.6, phase: cc.rnd(0, 10) }) },
    ], [2, 22]);
    const body = { kind: 'full', x: rnd(-110, 110), y: -rnd(110, 180), r: rnd(30, 40) };
    const flyers = flock(c, 'bat', 9, [40, 240], [4, 7]);
    return { far, near, body, flyers };
  },
};

// --- Sol ---------------------------------------------------------------------------------

/**
 * Sol du décor : le relief (bosses étalées sur toute la période de leur bande, tailles et
 * aplatissement selon la profondeur) et la graine de la texture (voir ui/groundPainter.js). Ce que
 * les bosses éclairent ou assombrissent dépend de la position de l'astre au moment du dessin.
 */
function generateGround(c, theme, worldSize) {
  const half = worldSize / 2;
  const mounds = [];
  const props = [];
  GROUND_BANDS.forEach((band, bi) => {
    const period = worldSize * band.parallax;
    const place = (i, n) => -period / 2 + ((i + c.rnd(0.1, 0.9)) * period) / n;
    for (let i = 0; !GROUND_FLAT.includes(theme) && i < band.mounds; i++) {
      const y = c.rnd(band.from * half, band.to * half);
      const w = c.rnd(240, 520) * groundScale(y / half);
      mounds.push({
        x: place(i, band.mounds), y, w, h: w * c.rnd(0.1, 0.18) * groundForeshortening(y / half), parallax: band.parallax,
      });
    }
    for (const [kind, spec] of Object.entries(GROUND_PROPS[theme] ?? {})) {
      const n = spec.counts[bi];
      for (let i = 0; i < n; i++) {
        const y = c.rnd(band.from * half, band.to * half);
        props.push({
          kind, x: place(i, n), y, w: spec.size * groundScale(y / half) * c.rnd(0.85, 1.15), parallax: band.parallax,
          phase: c.rnd(0, 6.28), flip: c.rnd(0, 1) < 0.5,
        });
      }
    }
  });
  props.sort((a, b) => a.y - b.y); // du plus lointain au plus proche : le plus proche recouvre
  return { worldSize, seed: c.rnd(0, 1000), mounds, props };
}

/** Hauteur de la ligne de crête (sommet le plus haut) à l'abscisse x, 0 hors des montagnes. */
export function ridgeHeight(peaks, x) {
  let top = 0;
  for (const p of peaks) {
    if (x <= p.x || x >= p.x + p.w) continue;
    const ax = p.x + p.w * p.apex;
    const y = x <= ax ? (p.h * (x - p.x)) / (ax - p.x) : (p.h * (p.x + p.w - x)) / (p.x + p.w - ax);
    top = Math.max(top, y);
  }
  return top;
}

/**
 * Flèches à faire clignoter : directions dans lesquelles se trouvent des fantômes à découvrir
 * hors du champ du viseur (axe dominant). Un fantôme déjà visible n'appelle aucune flèche.
 * @param {number} [worldSize]  pour l'écart en X par la couture (monde bouclé, voir camera.js#wrapX)
 * @returns {Set<'up'|'down'|'left'|'right'>}
 */
export function hintDirections(ghosts, camera, viewSize, worldSize, viewWidth = viewSize) {
  const half = viewSize / 2;
  const halfX = viewWidth / 2;
  const hints = new Set();
  for (const g of ghosts) {
    if (g.captured || g.known) continue;
    const dx = wrapX(g.x - camera.x, worldSize);
    const dy = g.y - camera.y;
    if (Math.abs(dx) <= halfX && Math.abs(dy) <= half) continue;
    if (Math.abs(dx) >= Math.abs(dy)) hints.add(dx > 0 ? 'right' : 'left');
    else hints.add(dy > 0 ? 'down' : 'up');
  }
  return hints;
}

/**
 * Coins du cadre de visée à faire clignoter : où sont les fantômes à découvrir hors champ.
 * Un fantôme au-dessus (sans être à gauche ni à droite) allume les deux coins du haut, un fantôme à
 * droite (sans être au-dessus ni en dessous) les deux coins de droite, etc. ; un fantôme en diagonale
 * (par exemple en haut à droite) n'allume que le coin de cette diagonale.
 * @returns {Set<'tl'|'tr'|'bl'|'br'>}
 */
export function hintCorners(ghosts, camera, viewSize, worldSize, viewWidth = viewSize) {
  const half = viewSize / 2;
  const halfX = viewWidth / 2;
  const corners = new Set();
  for (const g of ghosts) {
    if (g.captured || g.known) continue;
    const dx = wrapX(g.x - camera.x, worldSize);
    const dy = g.y - camera.y;
    const h = dx < -halfX ? 'l' : dx > halfX ? 'r' : '';
    const v = dy < -half ? 't' : dy > half ? 'b' : '';
    if (h && v) corners.add(v + h);
    else if (v) { corners.add(`${v}l`); corners.add(`${v}r`); }
    else if (h) { corners.add(`t${h}`); corners.add(`b${h}`); }
  }
  return corners;
}

